import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import adminFacilitatorService from '../../services/adminFacilitatorService';
import { toast } from 'react-toastify';

const EditFacilitatorModal = ({ facilitator, onClose, onSuccess, specializations }) => {
  const [isActive, setIsActive] = useState(facilitator.is_active);
  const [selectedSpecializations, setSelectedSpecializations] = useState(facilitator.specializations || []);
  const [maxActiveMatches, setMaxActiveMatches] = useState(facilitator.max_active_matches);
  const [bio, setBio] = useState(facilitator.bio || '');
  const [submitting, setSubmitting] = useState(false);

  const toggleSpecialization = (spec) => {
    if (selectedSpecializations.includes(spec)) {
      setSelectedSpecializations(selectedSpecializations.filter(s => s !== spec));
    } else {
      setSelectedSpecializations([...selectedSpecializations, spec]);
    }
  };

  const handleSubmit = async () => {
    if (selectedSpecializations.length === 0) {
      toast.warning('Please select at least one specialization');
      return;
    }

    if (maxActiveMatches < 1 || maxActiveMatches > 50) {
      toast.warning('Max active matches must be between 1 and 50');
      return;
    }

    setSubmitting(true);
    try {
      await adminFacilitatorService.updateFacilitator(facilitator.id, {
        is_active: isActive,
        specializations: selectedSpecializations,
        max_active_matches: maxActiveMatches,
        bio: bio.trim()
      });
      toast.success('Facilitator updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update facilitator');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #333'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #333',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: '#1a1a1a',
          zIndex: 1
        }}>
          <h2 style={{ color: '#FFB6C1', fontSize: '1.5rem', margin: 0 }}>
            Edit Facilitator
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem' }}>
          {/* Facilitator Info */}
          <div style={{
            backgroundColor: '#000',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: '#FFB6C1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#000'
              }}>
                {facilitator.user?.full_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <div style={{ color: '#FFB6C1', fontWeight: '600' }}>
                  {facilitator.user?.full_name}
                </div>
                <div style={{ color: '#999', fontSize: '0.9rem' }}>
                  {facilitator.user?.email}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ color: '#ccc' }}>
                Active (can accept new match requests)
              </span>
            </label>
          </div>

          {/* Specializations */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              color: '#FFB6C1',
              fontWeight: '600'
            }}>
              Specializations * (Select all that apply)
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '0.75rem'
            }}>
              {specializations.map(spec => (
                <label
                  key={spec}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    backgroundColor: selectedSpecializations.includes(spec) ? '#FFB6C120' : '#000',
                    border: `1px solid ${selectedSpecializations.includes(spec) ? '#FFB6C1' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedSpecializations.includes(spec)}
                    onChange={() => toggleSpecialization(spec)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ 
                    color: selectedSpecializations.includes(spec) ? '#FFB6C1' : '#ccc',
                    fontSize: '0.9rem',
                    textTransform: 'capitalize'
                  }}>
                    {spec.replace('_', ' ')}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Max Active Matches */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFB6C1',
              fontWeight: '600'
            }}>
              Maximum Active Matches
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={maxActiveMatches}
              onChange={(e) => setMaxActiveMatches(parseInt(e.target.value) || 10)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#999' }}>
              How many buddies can this facilitator support simultaneously?
            </p>
          </div>

          {/* Bio */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              color: '#FFB6C1',
              fontWeight: '600'
            }}>
              Bio (Optional)
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Write a brief bio for this facilitator..."
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#000',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#ccc',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#FFB6C1',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              <FontAwesomeIcon icon={faSave} /> {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditFacilitatorModal;
